import './FaultSelectionModal.scss'
import {
    FooterAction,
    HeaderAction,
    HeaderActionsContainer,
    HeaderTitle,
    HeaderTitleContainer,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from '../../../../../CommonComponents/modals/Modal';
import { Fragment, MouseEvent, useEffect, useMemo, useState } from 'react'
import useModal from '../../../hooks/useModal';
import useForm from '../../../hooks/useForm';
import { CloseSvg, EyeSvg } from '../../../../../CommonComponents/svgs/Svgs';
import { FaultsOptionType } from '../../../utils/scenario-types';
import { Form } from 'react-bootstrap';
import { preventEnterKey, replaceUnderscoresWithSpaces } from '../../../../../../utils/commonFunctions';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';
import { getTriggerInfo, renderTriggerContent } from './helperFunctions';
import { validateSameFaults } from '../../../utils/validation';
import useScenarioResponse from '../../../hooks/useScenarioResponse';
import useViewer from '../../../hooks/useViewer';

const FaultSelectionModal = () => {

    const { setFormData } = useForm()
    const { modals, setModals } = useModal()
    const { updateGroundPackets } = useViewer()
    const { scenarioResponse } = useScenarioResponse()

    const [detailsView, setDetailsView] = useState<FaultsOptionType | null>(null)

    const open = useMemo(() => modals?.configured?.name === 'fault', [modals?.configured?.name])
    const faultsOptions: FaultsOptionType[] = useMemo(() => modals?.configured?.data?.allFaults || [], [modals?.configured?.data?.allFaults])
    const checked: FaultsOptionType | null = useMemo(() => modals?.configured?.data?.selectedFault, [modals?.configured?.data?.selectedFault])

    useEffect(() => {
        const clickOutSide = (e: any) => {
            const modal = document.querySelector('.fault__details__modal')
            if (modal && !modal.contains(e.target as Node)) {
                setDetailsView(null)
            }
        }
        document.addEventListener('click', clickOutSide)
        return () => {
            document.removeEventListener('click', clickOutSide)
        }
    }, [])

    const closeModal = () => {
        setModals((prev) => ({
            ...prev,
            configured: null
        }))
    }

    const onSelect = (option: FaultsOptionType) => {
        setModals((prev) => ({
            ...prev!,
            configured: {
                ...prev.configured!,
                data: {
                    ...prev.configured?.data!,
                    selectedFault: prev?.configured?.data?.selectedFault?.faultPlanId === option?.faultPlanId ? null : option
                }
            }
        }))
    }

    const onSave = () => {
        if (!checked) return
        setFormData((prev) => {
            const filterFaults = prev.faults.filter((fault) => fault.faultPlanId !== checked?.faultPlanId)
            const faults = [
                ...filterFaults,
                {
                    satelliteGroupId: checked?.satelliteGroupId,
                    faultPlanId: checked?.faultPlanId,
                    faultPlanName: checked?.faultPlanName
                }
            ]

            // Commented out code for future reference if needed //
            // const status = prev.assets.faultPlans.data ? validateSameFaults(faults, scenarioResponse!) ? 'completed' : 'invalidated' : prev.assets.faultPlans.status
            const status = prev.assets.faultPlans.data ? 'invalidated' : prev.assets.faultPlans.status
            const assets = {
                ...prev.assets,
                faultPlans: {
                    ...prev.assets.faultPlans,
                    status: status
                }
            }

            const data = {
                ...prev,
                faults: faults,
                assets: assets
            }
            updateGroundPackets(data)
            return data
        })
        closeModal()
    }

    if (!open) return null
    return (
        <Fragment>
            <Modal open={open} pinned={true} disabled={true} className={`scenario__fault__selection__modal`}>
                <ModalHeader className='scenario__fault__selection__modal__header'>
                    <HeaderTitleContainer>
                        <HeaderTitle>
                            Fault Plans
                        </HeaderTitle>
                    </HeaderTitleContainer>
                    <HeaderActionsContainer>
                        <HeaderAction title='Close' onClick={closeModal}>
                            <CloseSvg />
                        </HeaderAction>
                    </HeaderActionsContainer>
                </ModalHeader>
                <ModalBody>
                    <div className='scenario__fault__selection__modal__body'>
                        {faultsOptions.map((option) => {
                            const openDetailsView = detailsView?.faultPlanId === option?.faultPlanId
                            return (
                                <div className='fault__list__item__container' key={option?.faultPlanId}
                                    onClick={() => onSelect(option)}
                                >
                                    <div className='fault__list__item__selection__container'>
                                        <Form.Check
                                            type={'radio'}
                                            label=""
                                            className="custom__checkbox__selection"
                                            checked={checked?.faultPlanId === option?.faultPlanId}
                                            onKeyDown={preventEnterKey}
                                        />
                                    </div>
                                    <div className='fault__list__item__details__container'>
                                        <span className='fault__list__item__text__container'>
                                            <CustomToolTip title={option?.faultPlanName} placement='top-start'>
                                                {option?.faultPlanName}
                                            </CustomToolTip>
                                        </span>
                                        <button className={`fault__details__button ${openDetailsView && 'active'}`}
                                            onClick={(event: MouseEvent) => {
                                                event.stopPropagation()
                                                setDetailsView((prev) => prev?.faultPlanId === option.faultPlanId ? null : option)
                                            }}
                                        >
                                            <EyeSvg />
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <FooterAction variant='outline__button'
                        onClick={closeModal}
                    >
                        Cancel
                    </FooterAction>
                    <FooterAction variant='primary__button'
                        disabled={!checked}
                        onClick={onSave}
                    >
                        Save
                    </FooterAction>
                </ModalFooter>
            </Modal>
            <Modal open={detailsView ? true : false} pinned={true} disabled={true} className={`fault__details__modal`}>
                <ModalHeader>
                    <HeaderTitleContainer>
                        <HeaderTitle>
                            {detailsView?.faultPlanName || ''}
                        </HeaderTitle>
                    </HeaderTitleContainer>
                    <HeaderActionsContainer>
                        <HeaderAction title='Close' onClick={() => setDetailsView(null)}>
                            <CloseSvg />
                        </HeaderAction>
                    </HeaderActionsContainer>
                </ModalHeader>
                <ModalBody>
                    <div className='fault__details__table'>
                        <div className='fault__details__table__header'>
                            <span className='fault__details__table__cell header__cell'>FAULTS</span>
                            <span className='fault__details__table__cell header__cell'>SUB-TYPE</span>
                            <span className='fault__details__table__cell header__cell'>TRIGGER</span>
                        </div>
                        <div className='fault__details__table__body'>
                            {detailsView?.faults?.map((fault, index) => {
                                const triggerInfo = replaceUnderscoresWithSpaces(getTriggerInfo(fault) ?? '');
                                const faultType = replaceUnderscoresWithSpaces(fault?.faultType ?? "NA");
                                const faultSystem = replaceUnderscoresWithSpaces(fault?.system ?? "NA");
                                const faultId = fault?.faultId ?? `fault-${index}`;
                                return (
                                    <div className='fault__details__table__body__row' key={faultId}>
                                        <div className='fault__details__table__cell'>
                                            <CustomToolTip placement='top-start' title={faultSystem}>
                                                <div className='faultplan__trigger__content'>
                                                    <span className='faultplan__trigger__text'>{faultSystem}</span>
                                                </div>
                                            </CustomToolTip>
                                        </div>
                                        <div className='fault__details__table__cell'>
                                            <CustomToolTip placement='top-start' title={faultType}>
                                                <div className='faultplan__trigger__content'>
                                                    <span className='faultplan__trigger__text'>{faultType}</span>
                                                </div>
                                            </CustomToolTip>
                                        </div>
                                        <div className='fault__details__table__cell'>
                                            <CustomToolTip placement='top-start' title={triggerInfo}>
                                                {renderTriggerContent(fault)}
                                            </CustomToolTip>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </ModalBody>
            </Modal>
        </Fragment>


    )

}

export default FaultSelectionModal